import { useCallback, useEffect, useState } from "react";
import { api, apiError, rupiah, tanggal, waktu, FILE_BASE } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { ErpLayout, Card, Table, Timeline } from "@/erp/shell";
import { toast } from "sonner";
import { Check, X, Eye, Download, Upload, RefreshCw, Pencil, Save, XCircle } from "lucide-react";

const inp = "w-full rounded-xl border border-[#E5DEC9] px-3 py-2 text-sm outline-none focus:border-[#C5A028]";
const lbl = "block text-xs font-bold uppercase tracking-wider text-[#4A4844] mb-1";

const Badge = ({ s }) => (
  <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${
    ["paid", "confirmed", "approved", "verified", "documents_complete", "completed"].includes(s)
      ? "bg-[#F5F1E8] text-[#15803D]"
      : ["rejected", "cancelled", "refunded", "revision_required"].includes(s)
        ? "bg-[#FBE9E9] text-[#B91C1C]" : "bg-[#F4EFE6] text-[#B45309]"}`}>
    {String(s || "-").replace(/_/g, " ")}
  </span>
);

// Module-level constants to prevent new array references on every render
const BOOKING_STATUSES = [
  "draft", "awaiting_payment", "payment_under_review", "partially_paid", "paid",
  "documents_incomplete", "documents_complete", "ready_to_depart", "departed",
  "completed", "cancelled", "refunded",
];
const BOOKING_COLUMNS = ["Kode", "Pemesan", "Paket", "Jamaah", "Tagihan", "Status", "Aksi"];
const BOOKING_BREADCRUMB = ["Booking & Jamaah"];
const PAYMENT_COLUMNS = ["Nomor", "Invoice", "Nominal", "Pengirim", "Tujuan", "Status", "Aksi"];
const PAYMENT_BREADCRUMB = ["Pembayaran Manual"];
const PAYOUT_COLUMNS = ["Nomor", "Mitra", "Nominal", "Rekening", "Status", "Aksi"];
const PAYOUT_BREADCRUMB = ["Mitra & Komisi", "Pencairan"];

const PILGRIM_FIELDS_ADMIN = [
  { key: "full_name_passport", label: "Nama Sesuai Paspor", type: "text", required: true },
  { key: "nik", label: "NIK", type: "text" },
  { key: "gender", label: "Jenis Kelamin", type: "select", opts: ["L", "P"] },
  { key: "birth_place", label: "Tempat Lahir", type: "text" },
  { key: "birth_date", label: "Tanggal Lahir", type: "date" },
  { key: "phone", label: "No. HP", type: "text" },
  { key: "email", label: "Email", type: "email" },
  { key: "address", label: "Alamat", type: "text" },
  { key: "passport_number", label: "No. Paspor", type: "text" },
  { key: "passport_expiry", label: "Kadaluarsa Paspor", type: "date" },
  { key: "relation_to_customer", label: "Hubungan", type: "text" },
  { key: "room_type", label: "Tipe Kamar", type: "select", opts: ["Quad", "Triple", "Double"] },
  { key: "emergency_contact_name", label: "Kontak Darurat", type: "text" },
  { key: "emergency_contact_phone", label: "No. Kontak Darurat", type: "text" },
  { key: "special_needs", label: "Kebutuhan Khusus", type: "text" },
];

function AdminPilgrimEditModal({ pilgrim, onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name_passport: pilgrim.full_name_passport || pilgrim.full_name || "",
    nik: pilgrim.nik || "",
    gender: pilgrim.gender || "L",
    birth_place: pilgrim.birth_place || "",
    birth_date: pilgrim.birth_date || "",
    phone: pilgrim.phone || "",
    email: pilgrim.email || "",
    address: pilgrim.address || "",
    passport_number: pilgrim.passport_number || "",
    passport_expiry: pilgrim.passport_expiry || "",
    relation_to_customer: pilgrim.relation_to_customer || "diri sendiri",
    room_type: pilgrim.room_type || "Quad",
    emergency_contact_name: pilgrim.emergency_contact_name || "",
    emergency_contact_phone: pilgrim.emergency_contact_phone || "",
    special_needs: pilgrim.special_needs || "",
  });
  const [busy, setBusy] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name_passport.trim()) { toast.error("Nama paspor wajib diisi"); return; }
    setBusy(true);
    try {
      await api.put(`/pilgrims/${pilgrim.id}`, form);
      toast.success("Data jamaah berhasil diperbarui");
      onSaved();
      onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid="admin-pilgrim-edit-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#E5DEC9] px-6 py-4">
          <h2 className="font-display text-lg font-bold text-[#141414]">Edit Jamaah — {pilgrim.full_name}</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-[#F4EFE6]" data-testid="admin-pilgrim-edit-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="max-h-[70vh] overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {PILGRIM_FIELDS_ADMIN.map((f) => (
              <div key={f.key} className={f.key === "address" || f.key === "special_needs" ? "sm:col-span-2" : ""}>
                <label className={lbl}>{f.label}{f.required && <span className="text-red-500 ml-1">*</span>}</label>
                {f.type === "select" ? (
                  <select className={inp} value={form[f.key]} onChange={(e) => setF(f.key, e.target.value)} data-testid={`admin-pilgrim-field-${f.key}`}>
                    {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={f.type} className={inp} value={form[f.key]} onChange={(e) => setF(f.key, e.target.value)}
                    data-testid={`admin-pilgrim-field-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-xl border border-[#E5DEC9] px-5 py-2 text-sm font-semibold hover:bg-[#F4EFE6]">Batal</button>
            <button type="submit" disabled={busy} className="flex items-center gap-2 rounded-xl bg-[#141414] px-5 py-2 text-sm font-semibold text-white hover:bg-[#2A2A2A] disabled:opacity-50" data-testid="admin-pilgrim-edit-save">
              <Save size={15} />{busy ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal Pembatalan Booking ─────────────────────────────────────────────────
function CancelBookingModal({ booking, onClose, onCancelled }) {
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${booking.id}/cancel-preview`)
      .then((r) => setPreview(r.data))
      .catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [booking.id, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { toast.error("Alasan pembatalan wajib diisi"); return; }
    if (!window.confirm(`Konfirmasi pembatalan booking ${booking.booking_code}?\nPotongan: ${preview?.deduction_pct}% (${rupiah(preview?.deduction_amount || 0)})\nRefund: ${rupiah(preview?.refund_amount || 0)}\n\nTindakan ini tidak dapat diurungkan.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/cancel`, { reason, note });
      toast.success(`Booking dibatalkan — Credit Note: ${data.credit_note.credit_note_number}`);
      onCancelled();
      onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid="cancel-booking-modal">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#E5DEC9] px-6 py-4">
          <h2 className="font-display text-lg font-bold text-[#141414]">Batalkan Booking</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-[#F4EFE6]"><X size={18} /></button>
        </div>
        <div className="px-6 py-5">
          {!preview ? (
            <p className="text-sm text-[#8A867C]">Menghitung kalkulasi...</p>
          ) : (
            <>
              {/* Info booking */}
              <div className="rounded-xl bg-[#F4EFE6] p-4 text-sm">
                <div className="font-semibold text-[#141414]">{preview.booking_code} — {preview.product_name}</div>
                <div className="mt-1 text-[#4A4844]">Berangkat: {preview.depart_date ? tanggal(preview.depart_date) : "—"}
                  {preview.days_left !== null && preview.days_left !== undefined &&
                    <span className="ml-2 text-xs font-semibold text-[#8C6D1F]">({preview.days_left} hari lagi)</span>}
                </div>
              </div>

              {/* Kalkulasi potongan */}
              <div className="mt-4 space-y-2 rounded-xl border border-[#E5DEC9] p-4 text-sm">
                <div className="flex justify-between"><span className="text-[#8A867C]">Sudah dibayar</span><span className="font-semibold">{rupiah(preview.paid_amount)}</span></div>
                <div className="flex justify-between">
                  <span className="text-[#8A867C]">Potongan ({preview.deduction_pct}% — {preview.policy_tier})</span>
                  <span className="font-semibold text-[#B91C1C]">− {rupiah(preview.deduction_amount)}</span>
                </div>
                <div className="flex justify-between border-t border-[#E5DEC9] pt-2">
                  <span className="font-bold">Refund ke Jamaah</span>
                  <span className="font-bold text-green-700">{rupiah(preview.refund_amount)}</span>
                </div>
                {preview.commission_clawback_total > 0 && (
                  <div className="mt-1 flex justify-between text-xs text-[#8A867C]">
                    <span>Clawback komisi ({preview.commission_entries?.length} entri)</span>
                    <span>{rupiah(preview.commission_clawback_total)}</span>
                  </div>
                )}
              </div>

              {/* Form alasan */}
              <form onSubmit={submit} className="mt-4 space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#4A4844]">Alasan Pembatalan <span className="text-red-500">*</span></label>
                  <select className={inp} data-testid="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} required>
                    <option value="">-- Pilih alasan --</option>
                    <option value="Permintaan jamaah">Permintaan jamaah</option>
                    <option value="Dokumen tidak lengkap">Dokumen tidak lengkap</option>
                    <option value="Kondisi kesehatan">Kondisi kesehatan</option>
                    <option value="Keadaan darurat keluarga">Keadaan darurat keluarga</option>
                    <option value="Pembatalan oleh operator">Pembatalan oleh operator</option>
                    <option value="Lainnya">Lainnya</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#4A4844]">Catatan tambahan</label>
                  <textarea rows={2} className={inp} data-testid="cancel-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opsional..." />
                </div>
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={onClose} className="flex-1 rounded-xl border border-[#E5DEC9] py-2 text-sm font-semibold hover:bg-[#F4EFE6]">Batal</button>
                  <button type="submit" disabled={busy || !reason}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#B91C1C] py-2 text-sm font-bold text-white hover:bg-[#991B1B] disabled:opacity-50"
                    data-testid="cancel-booking-submit">
                    <XCircle size={14} />{busy ? "Memproses..." : "Batalkan Booking"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function BookingAdmin() {
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [productId, setProductId] = useState("");
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [editPilgrim, setEditPilgrim] = useState(null);
  const [cancelBooking, setCancelBooking] = useState(null);

  useEffect(() => {
    api.get("/admin/products", { params: { page_size: 200 } })
      .then((r) => setProducts(r.data.items || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get("/bookings", { params: { q: q || undefined, status: status || undefined, product_id: productId || undefined, page, page_size: 10 } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [q, status, productId, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setDetail(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const open = async (id) => {
    try { const { data: d } = await api.get(`/bookings/${id}`); setDetail(d); }
    catch (e) { toast.error(apiError(e)); }
  };
  const confirm = async (id) => {
    try {
      const { data: d } = await api.post(`/bookings/${id}/confirm`);
      toast.success(d.idempotent ? "Booking sudah dikonfirmasi (idempotent, komisi tidak diduplikasi)"
        : `Booking dikonfirmasi. ${(d.commissions || []).length} entri komisi dibuat.`);
      load(); if (detail) open(id);
    } catch (e) { toast.error(apiError(e)); }
  };
  const setStat = async (id, s) => {
    try { await api.patch(`/bookings/${id}/status`, { status: s, note: `Diubah dari ERP` });
      toast.success(`Status → ${s}`); load(); if (detail) open(id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const pages = Math.max(1, Math.ceil(data.total / 10));

  return (
    <ErpLayout title="Booking & Jamaah" breadcrumb={BOOKING_BREADCRUMB}>
      {cancelBooking && (
        <CancelBookingModal
          booking={cancelBooking}
          onClose={() => setCancelBooking(null)}
          onCancelled={() => { load(); if (detail) setDetail(null); }}
        />
      )}
      {editPilgrim && (
        <AdminPilgrimEditModal
          pilgrim={editPilgrim}
          onClose={() => setEditPilgrim(null)}
          onSaved={() => detail && open(detail.id)}
        />
      )}
      <Card className="mb-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <input className={inp} placeholder="Cari kode/nama/email..." data-testid="booking-search"
            value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          <select className={inp} data-testid="booking-product-filter" value={productId}
            onChange={(e) => { setProductId(e.target.value); setPage(1); }}>
            <option value="">Semua paket</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select className={inp} data-testid="booking-status-filter" value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">Semua status</option>
            {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex items-center text-sm text-[#8A867C]">{data.total} booking</div>
        </div>
      </Card>
      <Table testid="booking-table" columns={BOOKING_COLUMNS}
        rows={data.items.map((b) => (
          <tr key={b.id} className="border-t border-[#F4EFE6]" data-testid={`booking-row-${b.id}`}>
            <td className="p-4"><div className="font-semibold">{b.booking_code}</div>
              <div className="text-xs text-[#8A867C]">{waktu(b.created_at)}</div></td>
            <td className="p-4">{b.contact_name}<div className="text-xs text-[#8A867C]">{b.contact_email}</div></td>
            <td className="p-4">{b.product_name}<div className="text-xs text-[#8A867C]">{b.depart_date ? tanggal(b.depart_date) : "-"}</div></td>
            <td className="p-4">{b.pax}</td>
            <td className="p-4">{rupiah(b.total_amount)}<div className="text-xs text-[#8A867C]">dibayar {rupiah(b.paid_amount)}</div></td>
            <td className="p-4"><Badge s={b.status} /></td>
            <td className="p-4"><div className="flex gap-2">
              <button onClick={() => open(b.id)} data-testid={`booking-view-${b.id}`} className="rounded-lg border border-[#E5DEC9] p-2"><Eye size={14} /></button>
              {can("booking.confirm") && b.status !== "confirmed" && (
                <button onClick={() => confirm(b.id)} data-testid={`booking-confirm-${b.id}`}
                  className="btn-gold rounded-lg px-3 py-1.5 text-xs font-bold">Konfirmasi</button>)}
            </div></td>
          </tr>))} />
      <div className="mt-4 flex items-center gap-2">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="booking-prev" className="rounded-lg border border-[#E5DEC9] bg-white px-3 py-2 text-sm disabled:opacity-40">Sebelumnya</button>
        <span className="text-sm">Halaman {page} dari {pages}</span>
        <button disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="booking-next" className="rounded-lg border border-[#E5DEC9] bg-white px-3 py-2 text-sm disabled:opacity-40">Berikutnya</button>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
          data-testid="booking-detail-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setDetail(null); }}>
          <Card className="my-8 w-full max-w-3xl" data-testid="booking-detail-panel">
            <div className="flex items-start justify-between">
              <div><div className="eyebrow">{detail.booking_code}</div>
                <h2 className="font-display text-xl font-bold text-[#141414]">{detail.product_name}</h2></div>
              <div className="flex items-center gap-2">
                {can("booking.confirm") && detail.status !== "confirmed" && !detail.commission_generated && (
                  <button onClick={() => confirm(detail.id)} data-testid="booking-detail-confirm"
                    className="btn-gold rounded-lg px-4 py-2 text-xs font-bold">Konfirmasi Booking</button>)}
                <button onClick={() => setDetail(null)} data-testid="booking-detail-close"><X size={18} /></button>
              </div>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3 text-sm">
              <div>Total: <b>{rupiah(detail.total_amount)}</b></div>
              <div>Dibayar: <b>{rupiah(detail.paid_amount)}</b></div>
              <div>Status: <Badge s={detail.status} /></div>
            </div>
            {detail.partner_snapshot ? (
              <div className="mt-4 rounded-lg bg-[#FAF7F0] p-4 text-sm" data-testid="booking-hierarchy-snapshot">
                <div className="eyebrow">Snapshot Hierarki Mitra</div>
                <div className="mt-1">Cabang: <b>{detail.partner_snapshot.branch_name}</b></div>
                {detail.partner_snapshot.subbranch_name && <div>Cabang Pembantu: <b>{detail.partner_snapshot.subbranch_name}</b></div>}
                <div>Consultant: <b>{detail.partner_snapshot.consultant_name || detail.partner_snapshot.partner_name}</b> ({detail.partner_snapshot.referral_code})</div>
              </div>
            ) : <div className="mt-4 rounded-lg bg-[#F4EFE6] p-3 text-sm text-[#8C6D1F]">Tanpa referral — tidak menghasilkan komisi otomatis.</div>}

            {detail.commissions?.length > 0 && (
              <div className="mt-4" data-testid="booking-commissions">
                <div className="eyebrow">Komisi (snapshot pool {rupiah(detail.commission_pool_snapshot)}/jamaah)</div>
                <table className="mt-2 w-full text-sm">
                  <tbody>
                    {detail.commissions.map((c) => (
                      <tr key={c.id} className="border-t border-[#F4EFE6]">
                        <td className="py-2 capitalize">{c.level.replace(/_/g, " ")}</td>
                        <td className="py-2">{c.partner_name}</td>
                        <td className="py-2">{c.percent}%</td>
                        <td className="py-2 font-semibold">{rupiah(c.amount)}</td>
                        <td className="py-2"><Badge s={c.status} /></td>
                      </tr>))}
                  </tbody>
                </table>
              </div>)}

            <div className="mt-5 flex items-center justify-between">
              <div className="eyebrow">Jamaah ({detail.pilgrims?.length || 0})</div>
            </div>
            <div className="mt-2 space-y-2" data-testid="booking-pilgrims">
              {(detail.pilgrims || []).map((p) => (
                <div key={p.id} className="flex items-start justify-between gap-2 rounded-lg border border-[#E5DEC9] p-3 text-sm">
                  <div className="min-w-0">
                    <div className="font-semibold">{p.full_name}</div>
                    <div className="text-xs text-[#8A867C]">NIK: {p.nik || "-"} · Paspor: {p.passport_number || "-"} · Kamar: {p.room_type}</div>
                    <div className="text-xs text-[#8A867C]">Kebutuhan khusus: {p.special_needs || "-"} · Dokumen: <span className="font-medium">{p.document_status}</span></div>
                  </div>
                  <button
                    onClick={() => setEditPilgrim(p)}
                    className="shrink-0 rounded-lg border border-[#E5DEC9] p-2 hover:bg-[#F4EFE6] hover:border-[#C5A028] transition-colors"
                    title="Edit data jamaah"
                    data-testid={`admin-pilgrim-edit-${p.id}`}
                  >
                    <Pencil size={14} className="text-[#C5A028]" />
                  </button>
                </div>))}
            </div>

            <div className="mt-5 eyebrow">Ubah Status</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {["documents_incomplete", "documents_complete", "ready_to_depart", "departed",
                "completed"].map((s) => (
                <button key={s} onClick={() => setStat(detail.id, s)} data-testid={`booking-status-${s}`}
                  className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold">{s.replace(/_/g, " ")}</button>))}
            </div>
            {/* Tombol Batalkan Booking — membuka modal dengan kalkulasi potongan */}
            {can("booking.write") && !["cancelled","refunded","completed","departed"].includes(detail.status) && (
              <div className="mt-4">
                <button onClick={() => setCancelBooking(detail)}
                  data-testid="btn-open-cancel-modal"
                  className="flex items-center gap-2 rounded-xl border border-[#F0C4C4] bg-red-50 px-4 py-2 text-sm font-semibold text-[#B91C1C] hover:bg-red-100">
                  <XCircle size={14} /> Batalkan Booking
                </button>
              </div>
            )}
            {/* Info credit note jika sudah dibatalkan */}
            {detail.credit_note_number && (
              <div className="mt-4 rounded-xl bg-[#FEF2F2] p-3 text-sm">
                <span className="font-semibold text-[#B91C1C]">Booking dibatalkan</span>
                <span className="ml-2 font-mono text-xs text-[#4A4844]">CN: {detail.credit_note_number}</span>
              </div>
            )}

            <div className="mt-5 eyebrow">Riwayat Status</div>
            <ol className="mt-2 space-y-2 text-sm" data-testid="booking-history">
              {(detail.status_history || []).map((h) => (
                <li key={`${h.status}-${h.at}`} className="border-l border-[#E5DEC9] pl-3">
                  <b className="capitalize">{String(h.status).replace(/_/g, " ")}</b>
                  <div className="text-xs text-[#8A867C]">{h.actor} · {waktu(h.at)} {h.note && `· ${h.note}`}</div>
                </li>))}
            </ol>
          </Card>
        </div>)}
    </ErpLayout>
  );
}

export function PaymentsAdmin() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const load = useCallback(() => {
    api.get("/payments", { params: { status: status || undefined } })
      .then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (p, action) => {
    let body = { note: "Diproses dari ERP" };
    if (action === "reject" || action === "reverse") {
      const reason = window.prompt(`Alasan ${action === "reject" ? "penolakan" : "pembalikan"}?`);
      if (!reason) return;
      body = { reason };
    }
    try {
      await api.post(`/payments/${p.id}/${action}`, body);
      toast.success(`Pembayaran ${action}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const proof = async (p) => {
    try {
      const { data } = await api.get(`/documents/${p.proof_file_id}/signed-url`);
      const a = document.createElement("a");
      a.href = `${FILE_BASE}${data.signed_url}`;
      a.target = "_blank";
      a.rel = "noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <ErpLayout title="Pembayaran Manual" breadcrumb={PAYMENT_BREADCRUMB}>
      <Card className="mb-5">
        <select className={`${inp} max-w-xs`} data-testid="payment-status-filter" value={status}
          onChange={(e) => setStatus(e.target.value)}>
          <option value="">Semua status</option>
          {["submitted", "under_review", "verified", "rejected", "reversed"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <p className="mt-3 text-xs text-[#8A867C]">Hanya pembayaran <b>verified</b> yang mengubah saldo invoice dan membuat jurnal. Verifikasi ganda dicegah sistem.</p>
      </Card>
      <Table testid="payment-table" columns={PAYMENT_COLUMNS}
        rows={rows.map((p) => (
          <tr key={p.id} className="border-t border-[#F4EFE6]" data-testid={`payment-row-${p.id}`}>
            <td className="p-4"><div className="font-semibold">{p.payment_number}</div>
              <div className="text-xs text-[#8A867C]">{p.transfer_date}</div></td>
            <td className="p-4">{p.invoice_number}</td>
            <td className="p-4 font-semibold">{rupiah(p.amount)}</td>
            <td className="p-4">{p.sender_name}<div className="text-xs text-[#8A867C]">{p.sender_bank} {p.reference_no && `· ${p.reference_no}`}</div></td>
            <td className="p-4">{p.company_bank_name}</td>
            <td className="p-4"><Badge s={p.status} />{p.rejection_reason && <div className="text-xs text-[#B91C1C]">{p.rejection_reason}</div>}</td>
            <td className="p-4"><div className="flex flex-wrap gap-2">
              {p.proof_file_id && <button onClick={() => proof(p)} data-testid={`payment-proof-${p.id}`} className="rounded-lg border border-[#E5DEC9] p-2"><Download size={13} /></button>}
              {can("payment.verify") && ["submitted", "under_review"].includes(p.status) && (<>
                <button onClick={() => act(p, "verify")} data-testid={`payment-verify-${p.id}`} className="btn-gold rounded-lg px-3 py-1.5 text-xs font-bold"><Check size={12} className="mr-1 inline" />Verifikasi</button>
                <button onClick={() => act(p, "reject")} data-testid={`payment-reject-${p.id}`} className="rounded-lg border border-[#F0C4C4] px-3 py-1.5 text-xs font-semibold text-[#B91C1C]">Tolak</button>
              </>)}
              {can("finance.write") && p.status === "verified" && (
                <button onClick={() => act(p, "reverse")} data-testid={`payment-reverse-${p.id}`} className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold"><RefreshCw size={12} className="mr-1 inline" />Reverse</button>)}
            </div></td>
          </tr>))} />
    </ErpLayout>
  );
}

export function PayoutAdmin() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/payouts").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/payouts/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (p, action) => {
    try {
      if (action === "reject") {
        const reason = window.prompt("Alasan penolakan?");
        if (!reason) return;
        await api.post(`/admin/payouts/${p.id}/reject`, { reason });
      } else if (action === "pay") {
        const input = document.createElement("input");
        input.type = "file";
        input.onchange = async () => {
          const fd = new FormData();
          fd.append("note", "Transfer dilakukan Finance");
          if (input.files[0]) fd.append("file", input.files[0]);
          try { await api.post(`/admin/payouts/${p.id}/pay`, fd); toast.success("Pencairan dibayar & jurnal dibuat"); load(); }
          catch (e) { toast.error(apiError(e)); }
        };
        input.click();
        return;
      } else {
        await api.post(`/admin/payouts/${p.id}/${action}`, { note: "Diproses dari ERP" });
      }
      toast.success(`Pencairan ${action}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <ErpLayout title="Pencairan Komisi" breadcrumb={PAYOUT_BREADCRUMB}>
      {summary && (
        <div className="mb-5 grid gap-4 sm:grid-cols-4" data-testid="payout-summary">
          <Card><div className="eyebrow">Komisi Payable</div><div className="mt-2 font-display text-2xl font-bold text-[#141414]">{rupiah(summary.payable)}</div></Card>
          <Card><div className="eyebrow">Sudah Dibayar</div><div className="mt-2 font-display text-2xl font-bold text-[#141414]">{rupiah(summary.paid)}</div></Card>
          <Card><div className="eyebrow">Per Level</div>
            <div className="mt-2 space-y-1 text-xs">{Object.entries(summary.by_level).map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="capitalize">{k.replace(/_/g, " ")}</span><b>{rupiah(v)}</b></div>))}</div></Card>
          <Card><div className="eyebrow">Per Status</div>
            <div className="mt-2 space-y-1 text-xs">{Object.entries(summary.by_status).map(([k, v]) => (
              <div key={k} className="flex justify-between"><span>{k}</span><b>{rupiah(v)}</b></div>))}</div></Card>
        </div>)}
      <Table testid="payout-table" columns={PAYOUT_COLUMNS}
        rows={rows.map((p) => (
          <tr key={p.id} className="border-t border-[#F4EFE6]" data-testid={`payout-row-${p.id}`}>
            <td className="p-4"><div className="font-semibold">{p.request_number}</div>
              <div className="text-xs text-[#8A867C]">{waktu(p.created_at)}</div></td>
            <td className="p-4">{p.partner_name}</td>
            <td className="p-4 font-semibold">{rupiah(p.amount)}</td>
            <td className="p-4 text-xs">{p.bank_name} {p.bank_account}<div>a.n. {p.account_holder}</div></td>
            <td className="p-4"><Badge s={p.status} />{p.rejection_reason && <div className="text-xs text-[#B91C1C]">{p.rejection_reason}</div>}</td>
            <td className="p-4"><div className="flex flex-wrap gap-2">
              {can("payout.review") && p.status === "submitted" && (
                <button onClick={() => act(p, "review")} data-testid={`payout-review-${p.id}`} className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold">Review</button>)}
              {can("payout.approve") && ["submitted", "under_review"].includes(p.status) && (
                <button onClick={() => act(p, "approve")} data-testid={`payout-approve-${p.id}`} className="btn-gold rounded-lg px-3 py-1.5 text-xs font-bold">Setujui</button>)}
              {can("payout.review") && !["paid", "cancelled", "rejected"].includes(p.status) && (
                <button onClick={() => act(p, "reject")} data-testid={`payout-reject-${p.id}`} className="rounded-lg border border-[#F0C4C4] px-3 py-1.5 text-xs font-semibold text-[#B91C1C]">Tolak</button>)}
              {can("payout.approve") && p.status === "approved" && (
                <button onClick={() => act(p, "pay")} data-testid={`payout-pay-${p.id}`} className="btn-gold rounded-lg px-3 py-1.5 text-xs font-bold"><Upload size={12} className="mr-1 inline" />Bayar + Bukti</button>)}
            </div></td>
          </tr>))} />
    </ErpLayout>
  );
}


// ─── Halaman Daftar Pembatalan ────────────────────────────────────────────────
const CANCEL_COLUMNS = ["No. CN", "Booking", "Jamaah", "Tgl Batal", "Dibayar", "Potongan", "Refund", "Clawback", "Diproses oleh"];

export function CancellationsAdmin() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => {
    api.get("/cancellations").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <ErpLayout title="Refund & Pembatalan" breadcrumb={["Refund & Pembatalan"]}>
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card><div className="eyebrow">Total Pembatalan</div><div className="mt-1 font-display text-2xl font-bold">{rows.length}</div></Card>
        <Card><div className="eyebrow">Total Dibayar</div><div className="mt-1 font-display text-2xl font-bold">{rupiah(rows.reduce((s,r)=>s+(r.paid_amount||0),0))}</div></Card>
        <Card><div className="eyebrow">Total Potongan</div><div className="mt-1 font-display text-2xl font-bold text-[#B91C1C]">{rupiah(rows.reduce((s,r)=>s+(r.deduction_amount||0),0))}</div></Card>
        <Card><div className="eyebrow">Total Refund</div><div className="mt-1 font-display text-2xl font-bold text-green-700">{rupiah(rows.reduce((s,r)=>s+(r.refund_amount||0),0))}</div></Card>
      </div>
      <Table testid="cancellation-table" columns={CANCEL_COLUMNS}
        rows={rows.map((c) => (
          <tr key={c.id} className="cursor-pointer border-t border-[#F4EFE6] hover:bg-[#FDFAF5]"
            data-testid={`cancel-row-${c.id}`} onClick={() => setSelected(c)}>
            <td className="p-4 font-mono text-xs font-bold text-[#8C6D1F]">{c.credit_note_number}</td>
            <td className="p-4 font-semibold">{c.booking_code}</td>
            <td className="p-4 text-sm">{c.customer_name || "—"}</td>
            <td className="p-4 text-sm">{tanggal(c.cancel_date)}</td>
            <td className="p-4">{rupiah(c.paid_amount)}</td>
            <td className="p-4 font-semibold text-[#B91C1C]">{c.deduction_pct}% — {rupiah(c.deduction_amount)}</td>
            <td className="p-4 font-semibold text-green-700">{rupiah(c.refund_amount)}</td>
            <td className="p-4 text-center text-xs">{c.clawback_count}</td>
            <td className="p-4 text-xs text-[#8A867C]">{c.created_by}</td>
          </tr>
        ))} />
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setSelected(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="cancellation-detail-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-[#141414]">{selected.credit_note_number}</h2>
              <button onClick={() => setSelected(null)}><X size={18} /></button>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              {[
                ["Booking", selected.booking_code],
                ["Tier kebijakan", selected.policy_tier],
                ["Hari sebelum berangkat", selected.days_left ?? "—"],
                ["Dibayar", rupiah(selected.paid_amount)],
              ].map(([k,v]) => <div key={k} className="flex justify-between"><span className="text-[#8A867C]">{k}</span><span className="font-semibold">{v}</span></div>)}
              <div className="flex justify-between border-t border-[#E5DEC9] pt-2"><span className="text-[#8A867C]">Potongan ({selected.deduction_pct}%)</span><span className="font-bold text-[#B91C1C]">− {rupiah(selected.deduction_amount)}</span></div>
              <div className="flex justify-between"><span className="font-bold">Refund</span><span className="font-bold text-green-700">{rupiah(selected.refund_amount)}</span></div>
              {[
                ["Alasan", selected.reason],
                ...(selected.note ? [["Catatan", selected.note]] : []),
                ["Clawback komisi", `${selected.clawback_count} entri`],
                ["Diproses oleh", selected.created_by],
                ["Tanggal", waktu(selected.created_at)],
              ].map(([k,v]) => <div key={k} className="flex justify-between border-t border-[#F4EFE6] pt-1"><span className="text-[#8A867C]">{k}</span><span>{v}</span></div>)}
            </div>
          </div>
        </div>
      )}
    </ErpLayout>
  );
}
